import { Alert, Button, Modal } from '@skupreme/shared/ui';
import { useState } from 'react';
import { useConfirmSuggestions } from '../../hooks/use-confirm-suggestions';
import {
  type BatchProgress,
  decisionOf,
  OUTCOME_WORDS,
  resultLine,
  summarize,
} from '../../lib/bulk';
import type {
  MappingSuggestionRow,
  RowOutcome,
} from '../../lib/suggestion-types';

export type BulkConfirmDialogProps = {
  /** The listings to link; the dialog shows while this is set. */
  rows: MappingSuggestionRow[] | null;
  /** `finished`: the run happened, so the page clears its selection. */
  onClose: (finished: boolean) => void;
};

const plural = (count: number, one: string, many: string) =>
  `${count} ${count === 1 ? one : many}`;

/** Asks once, links the listings 200 at a time with a progress line, then reports each row that did not link. */
export function BulkConfirmDialog({ rows, onClose }: BulkConfirmDialogProps) {
  const confirm = useConfirmSuggestions();
  const [progress, setProgress] = useState<BatchProgress | null>(null);
  const [outcomes, setOutcomes] = useState<RowOutcome[] | null>(null);

  /* Every set of listings opens on the question. */
  const [openedFor, setOpenedFor] = useState(rows);
  if (openedFor !== rows) {
    setOpenedFor(rows);
    setProgress(null);
    setOutcomes(null);
  }

  const decisions = (rows ?? []).flatMap(row => decisionOf(row) ?? []);
  const packs = decisions.filter(({ packQuantity }) => packQuantity > 1).length;
  const isRunning = confirm.isPending;

  const start = async () => {
    setProgress({ done: 0, total: decisions.length });
    const run = await confirm.mutateAsync({
      decisions,
      onProgress: setProgress,
    });
    setOutcomes(run.outcomes);
  };

  const unlinked = (outcomes ?? []).filter(
    ({ status }) => status !== 'mapped' && status !== 'unchanged',
  );
  const labelOf = (listingId: string) => {
    const row = rows?.find(candidate => candidate.listingId === listingId);

    return row?.sku ?? row?.title ?? listingId;
  };

  return (
    <Modal
      isOpen={!!rows}
      onOpenChange={open => !open && !isRunning && onClose(!!outcomes)}
      title={outcomes ? 'Done' : 'Link listings to products'}
      isDismissable={!isRunning}
      footer={
        <div className="sk:flex sk:justify-end sk:gap-2">
          {outcomes ? (
            <Button variant="primary" size="md" onPress={() => onClose(true)}>
              Close
            </Button>
          ) : (
            <>
              <Button
                variant="pill"
                size="md"
                isDisabled={isRunning}
                onPress={() => onClose(false)}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                size="md"
                isDisabled={isRunning || !decisions.length}
                onPress={start}
              >
                Link {decisions.length}
              </Button>
            </>
          )}
        </div>
      }
    >
      <div className="sk:flex sk:flex-col sk:gap-3">
        {outcomes ? (
          <>
            <Alert tone={unlinked.length ? 'warning' : 'success'}>
              {resultLine(summarize(outcomes))}
            </Alert>
            {unlinked.length ? (
              <ul className="sk:m-0 sk:flex sk:max-h-64 sk:list-none sk:flex-col sk:gap-1 sk:overflow-y-auto sk:p-0 sk:text-sm">
                {unlinked.map(({ listingId, status }) => (
                  <li key={listingId}>
                    <b>{labelOf(listingId)}</b>: {OUTCOME_WORDS[status]}
                  </li>
                ))}
              </ul>
            ) : null}
          </>
        ) : (
          <p className="sk:m-0 sk:text-sm sk:text-fg-secondary">
            Link {plural(decisions.length, 'listing', 'listings')} to products?
            {packs ? ` ${plural(packs, 'is a pack', 'are packs')}.` : ''}
          </p>
        )}
        {isRunning && progress ? (
          <p role="status" className="sk:m-0 sk:text-sm sk:text-fg-secondary">
            Linking… {progress.done} of {progress.total}
          </p>
        ) : null}
      </div>
    </Modal>
  );
}
